import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Checkbox } from"@/components/ui/checkbox";
import { Plus, FileText, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Sparkles, Eye, Trash2, ListOrdered } from"lucide-react";
import { toast } from"sonner";

const OffersPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("all");
 const [offers, setOffers] = useState([]);
 const [loading, setLoading] = useState(false);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [detailOpen, setDetailOpen] = useState(false);
 const [selectedOffer, setSelectedOffer] = useState(null);
 const [compareDialogOpen, setCompareDialogOpen] = useState(false);
 const [compareCategory, setCompareCategory] = useState("");
 const [aiComparison, setAiComparison] = useState("");
 const [comparingAi, setComparingAi] = useState(false);
 const [expandedCategories, setExpandedCategories] = useState({});
 const [projectJobs, setProjectJobs] = useState([]);
 const [materialSummaries, setMaterialSummaries] = useState({});
 const [pricingType, setPricingType] = useState("total");
 const [areaSource, setAreaSource] = useState("auto");
 const [selectedTasks, setSelectedTasks] = useState([]);
 const [mainTaskId, setMainTaskId] = useState("");
 const [formData, setFormData] = useState({
 company_name:"",
 contact_person:"",
 email:"",
 phone:"",
 service_type:"",
 description:"",
 amount: 0,
 currency:"EUR",
 validity_date:"",
 payment_terms:"",
 notes:"",
 file_url:"",
 price_per_m2: 0,
 area_m2: 0
 });
 const [editMode, setEditMode] = useState(false);
 const [editingOfferId, setEditingOfferId] = useState(null);
 const [credentialsDialog, setCredentialsDialog] = useState({ open: false, name: "", email: "", password: "" });
 const [pricingMode, setPricingMode] = useState("simple"); // "simple" or "line_items"
 const [lineItems, setLineItems] = useState([]);
 const [jobSubtasks, setJobSubtasks] = useState({});

 useEffect(() => {
 loadProjects();
 }, []);

 useEffect(() => {
 if (selectedProject && projects.length > 0) {
 loadOffers();
 loadProjectJobs();
 }
 }, [selectedProject, projects]);

 const loadProjectJobs = async () => {
 if (selectedProject ==="all"|| !selectedProject) {
 setProjectJobs([]);
 return;
 }
 try {
 const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/project-progress/${selectedProject}`);
 setProjectJobs(response.data);
 // Fetch material summaries for accurate m²
 try {
 const matRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/job-materials/summaries/${selectedProject}`);
 const map = {};
 Object.entries(matRes.data || {}).forEach(([jobId, summary]) => { map[jobId] = summary; });
 setMaterialSummaries(map);
 } catch {}
 } catch (error) {
 console.error("Failed to load project jobs", error);
 // If no jobs exist for this project, set empty array
 setProjectJobs([]);
 }
 };

 const loadJobSubtasks = async (jobId) => {
  try {
   const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/project-progress/jobs/${jobId}/subtasks`);
   setJobSubtasks(prev => ({ ...prev, [jobId]: res.data.subtasks || [] }));
  } catch { /* ignore */ }
 };

 const addLineItem = () => {
  setLineItems(prev => [...prev, {
   id: crypto.randomUUID(),
   subtask_ids: [],
   description: "",
   pricing_type: "per_m2",
   unit_price: 0,
   quantity: 0,
   total: 0
  }]);
 };

 const removeLineItem = (index) => {
  setLineItems(prev => prev.filter((_, i) => i !== index));
 };

 const updateLineItem = (index, field, value) => {
  setLineItems(prev => {
   const updated = [...prev];
   updated[index] = { ...updated[index], [field]: value };
   if (field === "pricing_type" && value === "total") {
    updated[index].quantity = 1;
    updated[index].total = updated[index].unit_price || 0;
   } else if (field === "unit_price" || field === "quantity") {
    updated[index].total = (updated[index].unit_price || 0) * (updated[index].quantity || 0);
   } else if (field === "subtask_ids") {
    // Auto-fill quantity from material summaries
    let autoQty = 0;
    for (const taskId of selectedTasks) {
     const summary = materialSummaries[taskId];
     if (summary?.subtasks) {
      for (const stId of value) {
       if (summary.subtasks[stId]) {
        autoQty += summary.subtasks[stId].total || 0;
       }
      }
     }
    }
    updated[index].quantity = Math.round(autoQty * 100) / 100;
    updated[index].total = (updated[index].unit_price || 0) * updated[index].quantity;
   }
   return updated;
  });
 };

 const lineItemsTotal = lineItems.reduce((sum, li) => sum + (li.total || 0), 0);

 // Get all subtasks across selected tasks
 const allSelectedSubtasks = selectedTasks.flatMap(taskId => {
  const task = projectJobs.find(j => j.id === taskId);
  return (jobSubtasks[taskId] || []).map(st => ({
   ...st,
   taskName: task?.job_name || ""
  }));
 });

 const handleTaskSelection = (taskId) => {
 let newSelectedTasks;
 if (selectedTasks.includes(taskId)) {
 // Deselect task
 newSelectedTasks = selectedTasks.filter(id => id !== taskId);
 if (mainTaskId === taskId && newSelectedTasks.length > 0) {
 // If removing main task, set first remaining task as main
 setMainTaskId(newSelectedTasks[0]);
 }
 } else {
 // Select task
 newSelectedTasks = [...selectedTasks, taskId];
 // If this is the first task, set it as main
 if (newSelectedTasks.length === 1) {
 setMainTaskId(taskId);
 }
 if (!jobSubtasks[taskId]) { loadJobSubtasks(taskId); }
 }
 setSelectedTasks(newSelectedTasks);
 
 // Update service_type based on main task
 const mainTask = projectJobs.find(j => j.id === (newSelectedTasks.length > 0 ? (mainTaskId || newSelectedTasks[0]) : null));
 if (mainTask) {
 setFormData(prev => ({ ...prev, service_type: mainTask.job_name }));
 }
 
 // Auto-fetch m² if pricing type is per_m2 and area source is auto
 if (pricingType ==="per_m2"&& areaSource ==="auto"&& mainTask) {
 const matSummary = materialSummaries[mainTask.id];
 const m2Value = matSummary ? matSummary.total : mainTask.area_m2;
 if (m2Value) {
 setFormData(prev => ({ ...prev, area_m2: m2Value }));
 } else {
 setFormData(prev => ({ ...prev, area_m2: 0 }));
 if (pricingType ==="per_m2") {
 toast.info("No m² defined for this job. Please add it in Project Progress > Materials or enter manually.");
 }
 }
 }
 };
 
 const handleServiceTypeChange = (jobName) => {
 const selectedJob = projectJobs.find(job => job.job_name === jobName);
 if (selectedJob) {
 setSelectedTasks([selectedJob.id]);
 setMainTaskId(selectedJob.id);
 setFormData({ ...formData, service_type: jobName });
 
 // Auto-fetch m² if pricing type is per_m2 and area source is auto
 if (pricingType ==="per_m2"&& areaSource ==="auto") {
 const matSummary = materialSummaries[selectedJob.id];
 const m2Value = matSummary ? matSummary.total : selectedJob.area_m2;
 if (m2Value) {
 setFormData(prev => ({ ...prev, service_type: jobName, area_m2: m2Value }));
 } else {
 setFormData(prev => ({ ...prev, service_type: jobName, area_m2: 0 }));
 if (pricingType ==="per_m2") {
 toast.info("No m² defined for this job. Please add it in Project Progress > Materials or enter manually.");
 }
 }
 }
 }
 };

 useEffect(() => {
 // Initialize all categories as collapsed by default
 const grouped = groupOffersByCategory();
 const initialExpanded = {};
 Object.keys(grouped).forEach(category => {
 initialExpanded[category] = false;
 });
 setExpandedCategories(initialExpanded);
 }, [offers]);

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 // Keep"all"as default - don't auto-select first project
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const loadOffers = async () => {
 setLoading(true);
 try {
 if (selectedProject ==="all") {
 // Load offers for all projects
 const allOffers = await Promise.all(
 projects.map(project => axios.get(`/offers/${project.id}`).catch(() => ({ data: [] })))
 );
 setOffers(allOffers.flatMap(response => response.data));
 } else {
 const response = await axios.get(`/offers/${selectedProject}`);
 setOffers(response.data);
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 
 // Validate task selection
 if (selectedTasks.length === 0) {
 toast.error("Please select at least one task");
 return;
 }

 // Validate line items if in line_items mode
 if (pricingMode === "line_items" && lineItems.length === 0) {
 toast.error("Please add at least one line item");
 return;
 }
 
 // Prepare the submission data
 const submissionData = {
 ...formData,
 validity_date: new Date(formData.validity_date).toISOString(),
 pricing_type: pricingMode === "line_items" ? "line_items" : pricingType,
 area_source: areaSource,
 task_ids: selectedTasks,
 main_task_id: mainTaskId || selectedTasks[0],
 line_items: pricingMode === "line_items" ? lineItems : []
 };
 
 // Calculate total based on pricing mode
 if (pricingMode === "line_items") {
 submissionData.amount = lineItemsTotal;
 } else if (pricingType ==="per_m2") {
 submissionData.amount = formData.price_per_m2 * formData.area_m2;
 }
 
 try {
 if (editMode && editingOfferId) {
 await axios.patch(`/offers/${editingOfferId}`, submissionData);
 toast.success(t("messages.updateSuccess"));
 } else {
 await axios.post("/offers", {
  ...submissionData,
  project_id: selectedProject
 });
 toast.success(t("messages.saveSuccess"));
 }
 
 setDialogOpen(false);
 setEditMode(false);
 setEditingOfferId(null);
 setPricingType("total");
 setPricingMode("simple");
 setAreaSource("auto");
 setSelectedTasks([]);
 setMainTaskId("");
 setLineItems([]);
 setFormData({
 company_name:"",
 contact_person:"",
 email:"",
 phone:"",
 service_type:"",
 description:"",
 amount: 0,
 currency:"EUR",
 validity_date:"",
 payment_terms:"",
 notes:"",
 file_url:"",
 price_per_m2: 0,
 area_m2: 0
 });
 loadOffers();
 } catch (error) {
 toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} offer`);
 }
 };

 const updateStatus = async (offerId, status) => {
 try {
 const response = await axios.patch(`/offers/${offerId}/status`, { status });
 
 if (status ==="accepted") {
 let message ="Offer accepted!";
 if (response.data.partner_created) {
 message +=" Partner automatically created.";
 }
 if (response.data.auto_rejected > 0) {
 message += ` ${response.data.auto_rejected} other offer(s) automatically rejected.`;
 }
 toast.success(message);
 // Show contractor login credentials if a new user account was created
 if (response.data.new_user) {
 setCredentialsDialog({
 open: true,
 name: response.data.new_user.name,
 email: response.data.new_user.email,
 password: response.data.new_user.password
 });
 }
 } else {
 toast.success(`Offer ${status}`);
 }
 
 loadOffers();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const startEdit = (offer) => {
 setEditingOfferId(offer.id);
 setEditMode(true);
 const offerPricingType = offer.pricing_type || "total";
 setPricingType(offerPricingType === "line_items" ? "total" : offerPricingType);
 setPricingMode(offerPricingType === "line_items" ? "line_items" : "simple");
 setAreaSource(offer.area_source ||"auto");
 
 // Set selected tasks
 if (offer.task_ids && offer.task_ids.length > 0) {
 setSelectedTasks(offer.task_ids);
 setMainTaskId(offer.main_task_id || offer.task_ids[0]);
 // Load subtasks for all selected tasks
 offer.task_ids.forEach(tid => { if (!jobSubtasks[tid]) loadJobSubtasks(tid); });
 } else {
 const task = projectJobs.find(j => j.job_name === offer.service_type);
 if (task) {
  setSelectedTasks([task.id]);
  setMainTaskId(task.id);
  if (!jobSubtasks[task.id]) loadJobSubtasks(task.id);
 } else {
  setSelectedTasks([]);
  setMainTaskId("");
 }
 }

 // Restore line items
 setLineItems(offer.line_items || []);
 
 setFormData({
 company_name: offer.company_name,
 contact_person: offer.contact_person,
 email: offer.email,
 phone: offer.phone,
 service_type: offer.service_type,
 description: offer.description,
 amount: offer.amount,
 currency: offer.currency,
 validity_date: offer.validity_date?.split('T')[0] ||"",
 payment_terms: offer.payment_terms ||"",
 notes: offer.notes ||"",
 file_url: offer.file_url ||"",
 price_per_m2: offer.price_per_m2 || 0,
 area_m2: offer.area_m2 || 0
 });
 setDialogOpen(true);
 };

 const handleFileUpload = async (e) => {
 const file = e.target.files[0];
 if (!file) return;

 // Check file type
 const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
 if (!allowedTypes.includes(file.type)) {
 toast.error(t("validation.required"));
 return;
 }

 // Check file size (max 5MB)
 if (file.size > 5 * 1024 * 1024) {
 toast.error(t("validation.required"));
 return;
 }

 const formData = new FormData();
 formData.append('file', file);

 try {
 toast.info("Uploading file...");
 const response = await axios.post('/upload', formData, {
 headers: { 'Content-Type': 'multipart/form-data' }
 });
 
 setFormData(prev => ({ ...prev, file_url: response.data.url }));
 toast.success(t("messages.saveSuccess"));
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const deleteOffer = async (offerId) => {
 if (!window.confirm("Are you sure you want to delete this offer?")) return;
 try {
 await axios.delete(`/offers/${offerId}`);
 toast.success(t("messages.deleteSuccess"));
 loadOffers();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const formatCurrency = (amount, currency ="EUR") => {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: currency,
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount || 0);
 };

 const formatDate = (dateString) => {
 if (!dateString) return"N/A";
 return new Date(dateString).toLocaleDateString('en-GB', {
 day: '2-digit',
 month: 'short',
 year: 'numeric'
 });
 };

 const groupOffersByCategory = () => {
 const grouped = {};
 offers.forEach(offer => {
 const category = offer.service_type ||"Other";
 if (!grouped[category]) {
 grouped[category] = [];
 }
 grouped[category].push(offer);
 });
 return grouped;
 };

 const toggleCategory = (category) => {
 setExpandedCategories(prev => ({
 ...prev,
 [category]: !prev[category]
 }));
 };

 const openOfferDetail = (offer) => {
 setSelectedOffer(offer);
 setDetailOpen(true);
 };

 const compareWithAI = async (category, categoryOffers) => {
 if (categoryOffers.length < 2) {
 toast.error(t("validation.required"));
 return;
 }

 setCompareCategory(category);
 setCompareDialogOpen(true);
 setComparingAi(true);
 setAiComparison("");

 try {
 const response = await axios.post("/ai/compare-offers", {
 category,
 offers: categoryOffers
 });
 setAiComparison(response.data.comparison);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 setAiComparison("Failed to generate comparison. Please try again.");
 } finally {
 setComparingAi(false);
 }
 };

 const getStatusBadge = (status) => {
 const styles = {
 pending:"bg-amber-500/10 text-amber-700 border-yellow-200/60",
 accepted:"bg-emerald-500/10 text-emerald-700 border-green-300",
 rejected:"bg-rose-500/10 text-rose-700 border-red-300"
 };
 return styles[status] || styles.pending;
 };

 const getStatusIcon = (status) => {
 if (status ==="accepted") return <CheckCircle className="w-4 h-4"/>;
 if (status ==="rejected") return <XCircle className="w-4 h-4"/>;
 return <Clock className="w-4 h-4"/>;
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 {t('offers.title')}
 </h1>
 <p className="text-slate-500 mt-1">{t('offers.subtitle')}</p>
 </div>
 <div className="flex gap-3 items-center">
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64">
 <SelectValue placeholder={t('projects.selectProject')} />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">{t('common.allProjects')}</SelectItem>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 <Button 
 onClick={() => {
 if (selectedProject ==="all") {
 toast.info(t('offers.selectProjectToAdd'));
 } else {
 setDialogOpen(true);
 }
 }}
 >
 <Plus className="w-4 h-4 mr-2"/>
 {t('offers.addOffer')}
 </Button>
 </div>
 </div>

 {/* Offers List - Categorized */}
 <Card>
 <CardHeader>
 <CardTitle>{t('offers.receivedOffers')}</CardTitle>
 </CardHeader>
 <CardContent>
 {loading ? (
 <div className="text-center py-12 text-slate-500">{t('common.loading')}</div>
 ) : offers.length === 0 ? (
 <div className="text-center py-12">
 <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-500">{t('offers.noOffers')}</p>
 </div>
 ) : (
 <div className="space-y-4">
 {Object.entries(groupOffersByCategory()).map(([category, categoryOffers]) => (
 <div key={category} className="border rounded-sm overflow-hidden">
 {/* Category Header */}
 <div 
 className="bg-white/60 p-4 cursor-pointer hover:bg-slate-200 transition-colors flex items-center justify-between"
 onClick={() => toggleCategory(category)}
 >
 <div className="flex items-center gap-3">
 <h3 className="text-lg font-semibold text-slate-900">{category}</h3>
 <Badge variant="outline"className="bg-white">
 {categoryOffers.length} {categoryOffers.length === 1 ? 'offer' : 'offers'}
 </Badge>
 </div>
 <div className="flex items-center gap-2">
 {categoryOffers.length > 1 && (
 <Button
 size="sm"
 onClick={(e) => {
 e.stopPropagation();
 compareWithAI(category, categoryOffers);
 }}
 className="bg-purple-600 hover:bg-purple-700"
 >
 <Sparkles className="w-4 h-4 mr-1"/>
 AI Compare
 </Button>
 )}
 {expandedCategories[category] ? (
 <ChevronUp className="w-5 h-5 text-slate-600"/>
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-600"/>
 )}
 </div>
 </div>

 {/* Category Offers */}
 {expandedCategories[category] && (
 <div className="p-4 space-y-3 bg-white">
 {categoryOffers.map((offer) => (
 <div 
 key={offer.id} 
 className="border rounded-sm p-4 hover:bg-white/80 transition-colors cursor-pointer"
 onClick={() => openOfferDetail(offer)}
 >
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-3 mb-2">
 <h4 className="text-base font-semibold text-slate-900">{offer.company_name}</h4>
 <Badge className={getStatusBadge(offer.status)}>
 <span className="flex items-center gap-1">
 {getStatusIcon(offer.status)}
 {offer.status}
 </span>
 </Badge>
 </div>
 <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
 <div>
 <span className="text-slate-600">Amount: </span>
 <span className="font-bold text-blue-700">{formatCurrency(offer.amount, offer.currency)}</span>
 {offer.line_items && offer.line_items.length > 0 && (
  <span className="text-xs text-slate-500 ml-1">({offer.line_items.length} line items)</span>
 )}
 </div>
 <div>
 <span className="text-slate-600">Valid Until: </span>
 <span className="text-slate-900">{formatDate(offer.validity_date)}</span>
 </div>
 <div>
 <span className="text-slate-600">Contact: </span>
 <span className="text-slate-900">{offer.contact_person}</span>
 </div>
 <div className="flex items-center gap-1 text-blue-600">
 <Eye className="w-3 h-3"/>
 <span className="text-xs">Click for details</span>
 </div>
 </div>
 </div>
 <div className="flex flex-col gap-2 ml-4"onClick={(e) => e.stopPropagation()}>
 {offer.status ==="pending"&& (
 <>
 <Button
 size="sm"
 variant="outline"
 onClick={() => updateStatus(offer.id,"accepted")}
 className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300"
 >
 <CheckCircle className="w-4 h-4 mr-1"/>
 Accept
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => updateStatus(offer.id,"rejected")}
 className="hover:bg-rose-50/80 hover:text-rose-700 hover:border-rose-300"
 >
 <XCircle className="w-4 h-4 mr-1"/>
 Reject
 </Button>
 </>
 )}
 <Button
 size="sm"
 variant="destructive"
 onClick={(e) => {
 e.stopPropagation();
 deleteOffer(offer.id);
 }}
 >
 Delete
 </Button>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Offer Detail Modal */}
 <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Offer Details</DialogTitle>
 </DialogHeader>
 {selectedOffer && (
 <div className="space-y-4">
 <div className="flex items-center gap-3">
 <h2 className="text-2xl font-bold text-slate-900">{selectedOffer.company_name}</h2>
 <Badge className={getStatusBadge(selectedOffer.status)}>
 <span className="flex items-center gap-1">
 {getStatusIcon(selectedOffer.status)}
 {selectedOffer.status}
 </span>
 </Badge>
 </div>

 <div className="bg-blue-50/80 p-4 rounded-sm border border-blue-200/60">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <p className="text-sm text-slate-600">Service Type</p>
 <p className="text-lg font-semibold text-slate-900">{selectedOffer.service_type}</p>
 {selectedOffer.task_ids && selectedOffer.task_ids.length > 1 && (
 <Badge className="mt-2 bg-blue-600 text-white">Bundled: {selectedOffer.task_ids.length} tasks</Badge>
 )}
 </div>
 <div>
 <p className="text-sm text-slate-600">Total Amount</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(selectedOffer.amount, selectedOffer.currency)}</p>
 {selectedOffer.pricing_type ==="per_m2"&& selectedOffer.price_per_m2 && (
  <p className="text-xs text-slate-600 mt-1">
  {formatCurrency(selectedOffer.price_per_m2, selectedOffer.currency)}/m² × {selectedOffer.area_m2} m²
  </p>
 )}
 {selectedOffer.pricing_type ==="line_items"&& selectedOffer.line_items && (
  <Badge className="mt-1 bg-slate-600 text-white text-xs">{selectedOffer.line_items.length} line items</Badge>
 )}
 </div>
 </div>
 </div>

 {selectedOffer.task_ids && selectedOffer.task_ids.length > 1 && (
 <div className="bg-green-50/80 p-3 rounded border border-green-200/60">
 <p className="text-sm font-semibold text-green-900 mb-2">Bundled Tasks</p>
 <div className="space-y-1">
 {selectedOffer.task_ids.map((taskId) => {
 const task = projectJobs.find(j => j.id === taskId);
 const isMain = taskId === selectedOffer.main_task_id;
 return task ? (
 <div key={taskId} className="flex items-center gap-2 text-sm">
 <span className={`${isMain ? 'font-semibold text-green-900' : 'text-[#334155]'}`}>
 • {task.job_name}
 </span>
 {isMain && <Badge className="bg-green-600 text-white text-xs">Main</Badge>}
 </div>
 ) : null;
 })}
 </div>
 <p className="text-xs text-slate-600 mt-2">The full cost will be shown on the main task in the Costs page.</p>
 </div>
 )}

 {/* Line Items Display */}
 {selectedOffer.line_items && selectedOffer.line_items.length > 0 && (
 <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="offer-detail-line-items">
  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
   <ListOrdered className="w-4 h-4" /> Line Items ({selectedOffer.line_items.length})
  </p>
  </div>
  <div className="divide-y divide-slate-100">
  {selectedOffer.line_items.map((li, idx) => (
   <div key={li.id || idx} className="px-4 py-3 flex items-center justify-between">
   <div className="flex-1">
    <p className="text-sm font-medium text-slate-900">{li.description || `Line Item ${idx + 1}`}</p>
    <p className="text-xs text-slate-500">
    {li.pricing_type === "per_m2" ? `${li.unit_price} ${selectedOffer.currency}/m²` : li.pricing_type === "per_piece" ? `${li.unit_price} ${selectedOffer.currency}/pc` : "Lump sum"} × {li.quantity}
    </p>
   </div>
   <p className="text-sm font-bold text-slate-900">{formatCurrency(li.total, selectedOffer.currency)}</p>
   </div>
  ))}
  </div>
  <div className="bg-slate-100 px-4 py-2 flex justify-between border-t border-slate-200">
  <p className="text-sm font-semibold text-slate-700">Grand Total</p>
  <p className="text-sm font-bold text-slate-900">{formatCurrency(selectedOffer.amount, selectedOffer.currency)}</p>
  </div>
 </div>
 )}

 <div className="grid grid-cols-2 gap-4">
 <div className="bg-white/50 p-3 rounded">
 <p className="text-xs text-slate-600 mb-1">Contact Person</p>
 <p className="font-medium text-slate-900">{selectedOffer.contact_person}</p>
 </div>
 <div className="bg-white/50 p-3 rounded">
 <p className="text-xs text-slate-600 mb-1">Phone</p>
 <p className="font-medium text-slate-900">{selectedOffer.phone}</p>
 </div>
 <div className="bg-white/50 p-3 rounded col-span-2">
 <p className="text-xs text-slate-600 mb-1">Email</p>
 <p className="font-medium text-slate-900">{selectedOffer.email}</p>
 </div>
 </div>

 <div>
 <p className="text-sm font-semibold text-[#334155] mb-2">Description</p>
 <p className="text-[#334155] bg-white/50 p-3 rounded">{selectedOffer.description ||"No description provided"}</p>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <p className="text-sm text-slate-600">Validity Date</p>
 <p className="font-medium text-slate-900">{formatDate(selectedOffer.validity_date)}</p>
 </div>
 <div>
 <p className="text-sm text-slate-600">Currency</p>
 <p className="font-medium text-slate-900">{selectedOffer.currency}</p>
 </div>
 </div>

 {selectedOffer.payment_terms && (
 <div className="bg-green-50/80 p-3 rounded border border-green-200/60">
 <p className="text-sm font-semibold text-green-900 mb-1">Payment Terms</p>
 <p className="text-sm text-[#334155]">{selectedOffer.payment_terms}</p>
 </div>
 )}

 {selectedOffer.notes && (
 <div className="bg-yellow-50/80 p-3 rounded border border-yellow-200/60">
 <p className="text-sm font-semibold text-yellow-900 mb-1">Notes</p>
 <p className="text-sm text-[#334155]">{selectedOffer.notes}</p>
 </div>
 )}

 {selectedOffer.file_url && (
 <div className="bg-blue-50/80 p-3 rounded border border-blue-200/60">
 <p className="text-sm font-semibold text-blue-900 mb-2">Attached Document</p>
 <a 
 href={selectedOffer.file_url} 
 target="_blank"
 rel="noopener noreferrer"
 className="text-blue-600 hover:underline flex items-center gap-2"
 >
 <FileText className="w-4 h-4"/>
 View Document
 </a>
 </div>
 )}

 <div className="flex gap-3 pt-4 border-t">
 {selectedOffer.status ==="pending"&& (
 <>
 <Button
 onClick={() => {
 updateStatus(selectedOffer.id,"accepted");
 setDetailOpen(false);
 }}
 className="flex-1 bg-green-600 hover:bg-green-700"
 >
 <CheckCircle className="w-4 h-4 mr-2"/>
 Accept Offer
 </Button>
 <Button
 variant="outline"
 onClick={() => {
 updateStatus(selectedOffer.id,"rejected");
 setDetailOpen(false);
 }}
 className="flex-1 text-red-600 border-red-300 hover:bg-red-50/80"
 >
 <XCircle className="w-4 h-4 mr-2"/>
 Reject Offer
 </Button>
 </>
 )}
 <Button
 variant="outline"
 onClick={() => {
 startEdit(selectedOffer);
 setDetailOpen(false);
 }}
 className={selectedOffer.status ==="pending"?"":"flex-1"}
 >
 Edit
 </Button>
 <Button
 variant="destructive"
 onClick={() => {
 deleteOffer(selectedOffer.id);
 setDetailOpen(false);
 }}
 >
 Delete
 </Button>
 <Button
 variant="outline"
 onClick={() => setDetailOpen(false)}
 >
 Close
 </Button>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>

 {/* AI Comparison Modal */}
 <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
 <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <Sparkles className="w-5 h-5 text-purple-600"/>
 AI Comparison - {compareCategory}
 </DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 {comparingAi ? (
 <div className="text-center py-12">
 <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
 <p className="text-slate-600">AI is analyzing the offers...</p>
 </div>
 ) : aiComparison ? (
 <div className="prose max-w-none">
 <div className="bg-purple-50/80 border border-purple-200 rounded-sm p-4">
 <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">
 {aiComparison}
 </pre>
 </div>
 </div>
 ) : null}
 <div className="flex justify-end pt-4 border-t">
 <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>

 {/* Add/Edit Offer Dialog */}
 <Dialog open={dialogOpen} onOpenChange={(open) => {
 setDialogOpen(open);
 if (!open) {
  setEditMode(false);
  setEditingOfferId(null);
  setPricingMode("simple");
  setLineItems([]);
 }
 }}>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>{editMode ?"Edit Offer":"Add New Offer"}</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="company_name">Company Name *</Label>
 <Input
 id="company_name"
 value={formData.company_name}
 onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="contact_person">Contact Person *</Label>
 <Input
 id="contact_person"
 value={formData.contact_person}
 onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
 required
 />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="email">Email *</Label>
 <Input
 id="email"
 type="email"
 value={formData.email}
 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="phone">Phone *</Label>
 <Input
 id="phone"
 value={formData.phone}
 onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
 required
 />
 </div>
 </div>
 <div>
 <Label className="mb-2 block">Select Task(s) for Offer *</Label>
 <div className="border rounded-sm p-4 max-h-64 overflow-y-auto bg-white/50">
 {projectJobs.length === 0 ? (
 <p className="text-sm text-slate-500">No tasks available. Initialize project progress first.</p>
 ) : (
 <div className="space-y-2">
 {projectJobs.map((job) => {
 const phaseNames = {
 1:"Pre-Construction",
 2:"Shell & Core",
 3:"Fit-Out",
 4:"Completion"
 };
 const isSelected = selectedTasks.includes(job.id);
 const isMain = mainTaskId === job.id;
 
 return (
 <div key={job.id} className="flex items-start gap-3 p-2 hover:bg-white rounded transition-colors">
 <Checkbox
 id={`task-${job.id}`}
 checked={isSelected}
 onCheckedChange={() => handleTaskSelection(job.id)}
 />
 <div className="flex-1 flex items-start justify-between">
 <div>
 <label htmlFor={`task-${job.id}`} className="text-sm font-medium text-slate-900 cursor-pointer">
 {job.job_name}
 </label>
 <p className="text-[11px] text-slate-500">Phase {job.phase}: {phaseNames[job.phase]}</p>
 </div>
 {isSelected && (
 <div className="flex items-center gap-2">
 {isMain ? (
 <Badge className="bg-blue-600 text-white text-xs">Main Task</Badge>
 ) : (
 <Button
 type="button"
 size="sm"
 variant="outline"
 onClick={() => setMainTaskId(job.id)}
 className="h-6 text-xs"
 >
 Set as Main
 </Button>
 )}
 </div>
 )}
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 {selectedTasks.length > 1 && (
 <p className="text-xs text-blue-600 mt-2">
 💡 Bundled offer: {selectedTasks.length} tasks selected. The full cost will be shown on the main task.
 </p>
 )}
 </div>
 <div>
 <Label htmlFor="validity_date">Valid Until *</Label>
 <Input
 id="validity_date"
 type="date"
 value={formData.validity_date}
 onChange={(e) => setFormData({ ...formData, validity_date: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="description">Description *</Label>
 <Textarea
 id="description"
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 rows={3}
 required
 />
 </div>
 {/* Pricing Mode Toggle */}
 <div className="border rounded-sm p-4 bg-white/50" data-testid="pricing-mode-toggle">
 <Label className="mb-2 block">Pricing Mode *</Label>
 <div className="flex gap-2">
  <Button type="button" variant={pricingMode ==="simple"?"default":"outline"} onClick={() => setPricingMode("simple")} className="flex-1">
  Simple Price
  </Button>
  <Button type="button" variant={pricingMode ==="line_items"?"default":"outline"} onClick={() => setPricingMode("line_items")} className="flex-1" data-testid="line-items-mode-btn">
  <ListOrdered className="w-4 h-4 mr-1" /> Line Items
  </Button>
 </div>
 </div>

 {pricingMode === "simple" ? (
 <>
 {/* Simple Pricing Type Toggle */}
 <div className="border rounded-sm p-4 bg-white/50">
  <Label className="mb-2 block">Pricing Method *</Label>
  <div className="flex gap-2">
  <Button type="button" variant={pricingType ==="total"?"default":"outline"} onClick={() => setPricingType("total")} className="flex-1">
   Total Price
  </Button>
  <Button type="button" variant={pricingType ==="per_m2"?"default":"outline"} onClick={() => setPricingType("per_m2")} className="flex-1">
   Price per m²
  </Button>
  </div>
 </div>

 {pricingType ==="total"? (
  <div className="grid grid-cols-2 gap-4">
  <div>
   <Label htmlFor="amount">Total Amount *</Label>
   <Input id="amount" type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })} required />
  </div>
  <div>
   <Label htmlFor="currency">{t("projectProgress.currency")}</Label>
   <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
   <SelectTrigger><SelectValue /></SelectTrigger>
   <SelectContent>
    <SelectItem value="EUR">EUR</SelectItem>
    <SelectItem value="USD">USD</SelectItem>
    <SelectItem value="MKD">MKD</SelectItem>
   </SelectContent>
   </Select>
  </div>
  </div>
 ) : (
  <div className="space-y-4">
  <div className="border rounded-sm p-4 bg-blue-50/80">
   <Label className="mb-2 block">Area (m²) Source *</Label>
   <div className="flex gap-2">
   <Button type="button" variant={areaSource ==="auto"?"default":"outline"} onClick={() => { setAreaSource("auto"); if (formData.service_type) { handleServiceTypeChange(formData.service_type); } }} className="flex-1">
    Auto (from phase)
   </Button>
   <Button type="button" variant={areaSource ==="manual"?"default":"outline"} onClick={() => setAreaSource("manual")} className="flex-1">
    Manual Entry
   </Button>
   </div>
  </div>
  <div className="grid grid-cols-3 gap-4">
   <div>
   <Label htmlFor="price_per_m2">Price per m² *</Label>
   <Input id="price_per_m2" type="number" step="0.01" value={formData.price_per_m2} onChange={(e) => setFormData({ ...formData, price_per_m2: parseFloat(e.target.value) || 0 })} required />
   </div>
   <div>
   <Label htmlFor="area_m2">Area (m²) *</Label>
   <Input id="area_m2" type="number" step="0.01" value={formData.area_m2} onChange={(e) => setFormData({ ...formData, area_m2: parseFloat(e.target.value) || 0 })} disabled={areaSource ==="auto"} required className={areaSource ==="auto"?"bg-white/60":""} />
   </div>
   <div>
   <Label htmlFor="currency">{t("projectProgress.currency")}</Label>
   <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
    <SelectTrigger><SelectValue /></SelectTrigger>
    <SelectContent>
    <SelectItem value="EUR">EUR</SelectItem>
    <SelectItem value="USD">USD</SelectItem>
    <SelectItem value="MKD">MKD</SelectItem>
    </SelectContent>
   </Select>
   </div>
  </div>
  <div className="bg-green-50/80 border border-green-200/60 p-3 rounded">
   <div className="text-sm text-slate-600">Calculated Total</div>
   <div className="text-2xl font-bold text-slate-900">
   {new Intl.NumberFormat('en-US', { style: 'currency', currency: formData.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(formData.price_per_m2 * formData.area_m2)}
   </div>
   <div className="text-[11px] text-slate-500 mt-1">{formData.price_per_m2} {formData.currency}/m² × {formData.area_m2} m²</div>
  </div>
  </div>
 )}
 </>
 ) : (
 /* Line Items Pricing Mode */
 <div className="space-y-3" data-testid="line-items-section">
  <div className="flex items-center justify-between">
  <Label className="block">Line Items</Label>
  <div className="flex items-center gap-2">
   <Label className="text-xs text-slate-500">Currency</Label>
   <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
   <SelectTrigger className="w-20 h-8 text-xs"><SelectValue /></SelectTrigger>
   <SelectContent>
    <SelectItem value="EUR">EUR</SelectItem>
    <SelectItem value="USD">USD</SelectItem>
    <SelectItem value="MKD">MKD</SelectItem>
   </SelectContent>
   </Select>
  </div>
  </div>

  {allSelectedSubtasks.length === 0 && selectedTasks.length > 0 && (
  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">No subtasks found for selected task(s). Create subtasks first in Project Progress.</p>
  )}

  {lineItems.map((li, idx) => (
  <div key={li.id} className="border rounded-sm p-3 bg-white/60 space-y-2" data-testid={`line-item-${idx}`}>
   <div className="flex items-center justify-between">
   <span className="text-xs font-semibold text-slate-500">Line Item {idx + 1}</span>
   <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(idx)} className="h-6 w-6 p-0 text-red-400 hover:text-red-600" data-testid={`remove-line-item-${idx}`}>
    <Trash2 className="w-3.5 h-3.5" />
   </Button>
   </div>
   <div>
   <Label className="text-xs">Description</Label>
   <Input value={li.description} onChange={(e) => updateLineItem(idx, "description", e.target.value)} placeholder="e.g., Bathroom floor tiles" className="h-8 text-sm" data-testid={`line-item-desc-${idx}`} />
   </div>
   <div>
   <Label className="text-xs">Subtask(s)</Label>
   <div className="flex flex-wrap gap-1 mt-1">
    {allSelectedSubtasks.map(st => {
    const isActive = li.subtask_ids.includes(st.id);
    return (
     <button type="button" key={st.id} onClick={() => {
     const newIds = isActive ? li.subtask_ids.filter(id => id !== st.id) : [...li.subtask_ids, st.id];
     updateLineItem(idx, "subtask_ids", newIds);
     }} className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${isActive ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`} data-testid={`line-item-subtask-${idx}-${st.id}`}>
     {st.name}
     </button>
    );
    })}
   </div>
   </div>
   <div className="grid grid-cols-3 gap-2">
   <div>
    <Label className="text-xs">Type</Label>
    <Select value={li.pricing_type} onValueChange={(v) => updateLineItem(idx, "pricing_type", v)}>
    <SelectTrigger className="h-8 text-xs" data-testid={`line-item-type-${idx}`}><SelectValue /></SelectTrigger>
    <SelectContent>
     <SelectItem value="per_m2">per m²</SelectItem>
     <SelectItem value="per_piece">per piece</SelectItem>
     <SelectItem value="total">lump sum</SelectItem>
    </SelectContent>
    </Select>
   </div>
   <div>
    <Label className="text-xs">{li.pricing_type === "total" ? "Amount" : "Unit Price"}</Label>
    <Input type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLineItem(idx, "unit_price", parseFloat(e.target.value) || 0)} className="h-8 text-sm" data-testid={`line-item-price-${idx}`} />
   </div>
   <div>
    <Label className="text-xs">{li.pricing_type === "per_m2" ? "m²" : li.pricing_type === "per_piece" ? "Pieces" : "Qty"}</Label>
    <Input type="number" step="0.01" value={li.quantity} onChange={(e) => updateLineItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="h-8 text-sm" disabled={li.pricing_type === "total"} data-testid={`line-item-qty-${idx}`} />
   </div>
   </div>
   <div className="text-right text-sm font-semibold text-slate-700">
   Subtotal: {formatCurrency(li.total, formData.currency)}
   </div>
  </div>
  ))}

  <Button type="button" variant="outline" onClick={addLineItem} className="w-full border-dashed" data-testid="add-line-item-btn">
  <Plus className="w-4 h-4 mr-1" /> Add Line Item
  </Button>

  {lineItems.length > 0 && (
  <div className="bg-emerald-50 border border-emerald-200 p-3 rounded flex justify-between items-center" data-testid="line-items-grand-total">
   <span className="text-sm font-semibold text-emerald-800">Grand Total</span>
   <span className="text-xl font-bold text-emerald-900">{formatCurrency(lineItemsTotal, formData.currency)}</span>
  </div>
  )}
 </div>
 )}
 <div>
 <Label htmlFor="payment_terms">Payment Terms</Label>
 <Textarea
 id="payment_terms"
 value={formData.payment_terms}
 onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
 placeholder="e.g., 50% upfront, 50% on completion"
 rows={2}
 />
 </div>
 <div>
 <Label htmlFor="file">Attach Offer Document (PDF/JPG)</Label>
 <Input
 id="file"
 type="file"
 accept=".pdf,.jpg,.jpeg,.png"
 onChange={handleFileUpload}
 className="cursor-pointer"
 />
 {formData.file_url && (
 <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
 <CheckCircle className="w-3 h-3"/>
 File uploaded successfully
 </p>
 )}
 </div>
 <div>
 <Label htmlFor="notes">{t("common.notes")}</Label>
 <Textarea
 id="notes"
 value={formData.notes}
 onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
 rows={2}
 />
 </div>
 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1">{editMode ?"Update Offer":"Add Offer"}</Button>
 <Button type="button"variant="outline"onClick={() => setDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>

 {/* Contractor Credentials Dialog — shown after offer acceptance */}
 <Dialog open={credentialsDialog.open} onOpenChange={(o) => setCredentialsDialog(p => ({ ...p, open: o }))}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle className="font-display uppercase tracking-tight text-emerald-700 flex items-center gap-2">
 <CheckCircle className="w-5 h-5" /> Contractor Account Created
 </DialogTitle>
 </DialogHeader>
 <div className="space-y-4 py-2">
 <p className="text-sm text-slate-600">
 A SitePulse login has been automatically created for <strong>{credentialsDialog.name}</strong>. Share these credentials with the contractor so they can submit progress reports and invoices.
 </p>
 <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3">
 <div>
 <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Email</p>
 <p className="font-mono text-sm text-slate-900 select-all">{credentialsDialog.email}</p>
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Temporary Password</p>
 <p className="font-mono text-sm text-slate-900 select-all bg-amber-50 border border-amber-200 rounded px-2 py-1">{credentialsDialog.password}</p>
 </div>
 </div>
 <p className="text-xs text-amber-600 flex items-center gap-1">
 <Clock className="w-3.5 h-3.5" /> Save these credentials now — the password won't be shown again.
 </p>
 </div>
 <div className="flex justify-end pt-2">
 <Button onClick={() => setCredentialsDialog(p => ({ ...p, open: false }))} className="bg-slate-900 hover:bg-slate-800">
 Done
 </Button>
 </div>
 </DialogContent>
 </Dialog>

 </div>
 </Layout>
 );
};

export default OffersPage;
// Force rebuild at Sat Dec 6 08:45:59 UTC 2025
